/**
 * OutstandingAccount â€” past-visits balance section.
 *
 * Renders the patient's `open_balances` from `/payment/me`. The API
 * returns one row per procedure; we group by `visit_id` for selection
 * (the checkbox stays at visit level per the data-layer contract:
 * `visit_ids: number[]` on submit, no per-CPT partial). Within each
 * visit group, procedures appear as sub-lines when there are 2+ â€”
 * addressing QA TC-014 / TC-022 / TC-023 which flagged the previous
 * fully-collapsed view as lossy.
 *
 * **Display fields per QA Section A.1 + v0.7.2 patient-facing refinements:**
 *
 *   DOS           â† date_of_service     (visit-level)
 *   Provider      â† provider_name       (visit-level)
 *   Facility      â† facility_name       (visit-level; truncated if long)
 *   Service Type  â† appointment_type    (visit-level; null-safe omission)
 *   Payer         â† payer_name          (visit-level; truncated if long)
 *   Insurance     â† ins_paid            (omit when 0)
 *   Patient Paid  â† patient_paid        (omit when 0)
 *   Balance       â† patient_balance     (drives the checkbox total)
 *
 * **Deliberately NOT rendered** per v0.7.2 QA feedback:
 *
 *   - `billed` â€” patients perceive sticker price as inflated even when
 *     it's contractual. We show what insurance covered + what the
 *     patient owes; the gross-billed number aggravates without
 *     informing. Backend still returns it; UI just doesn't surface it.
 *   - CPT codes themselves (e.g. `99214`) â€” patients don't recognize
 *     them. We render the human description and truncate when long
 *     (via CSS `text-overflow: ellipsis`); hovering shows the full
 *     description via the `title` attribute.
 *
 * **Defensive null handling** â€” every visit-level identity field
 * (provider, facility, appointment_type, payer) is filtered through
 * `.filter(Boolean)` before joining with `Â·`, so a null `appointment_type`
 * (the common case until backend's CRM-lookup gap closes) drops
 * cleanly with no orphaned separator. Long facility / payer names
 * truncate via CSS â€” see `MAX_INLINE_CHARS`.
 *
 * **Layout â€” two-tier responsive:**
 *
 *   Desktop (â‰¥768px): single horizontal row per visit. Identity metadata
 *   sits on a sub-line via `Â·` separators. Money right-aligned with
 *   `tabular-nums`. CPT sub-lines appear below for multi-procedure visits.
 *
 *   Mobile (<540px): identity metadata stacks one field per line,
 *   separators hidden. Balance flows next to DOS rather than to a
 *   right rail. Money trio stacks under identity.
 */
import { Card, CardContent } from '@tensaw/design-system/layout';
import { Checkbox } from '@tensaw/design-system/primitives';

import type { OpenBalance } from '../../api/types';
import { format, sumCents } from '../../lib/money';

// /**
//  * Soft truncation threshold for identity-line fields (facility, payer).
//  * Below this, the text renders in full. At or above, we let CSS
//  * `text-overflow: ellipsis` clip the visible portion; the full string
//  * stays in the `title` attribute for hover. Chosen by visual fit: most
//  * payer names are short codes ("Aetna", "BCBS", "UHC") and pass
//  * through; "BlueCross BlueShield of Texas" and similar will clip.
//  *
//  * We don't hard-truncate at this length â€” CSS handles the visible
//  * boundary which respects viewport width. The flag is just a hint to
//  * apply the `max-width` constraint that enables the ellipsis.
//  */
// const LONG_FIELD_CHARS = 18;

interface OutstandingAccountProps {
  openBalances: OpenBalance[];
  /** Set of visit_ids currently selected. */
  selectedVisitIds: Set<number>;
  onSelectionChange: (next: Set<number>) => void;
}

/**
 * One visit's worth of data. Identity fields (`date_of_service`,
 * `provider_name`, `facility_name`, `appointment_type`, `payer_name`)
 * are pulled from the first procedure row â€” they're per-claim in
 * reality but identical across all procedures of a claim, so taking
 * the first works in practice. Money totals are summed across the
 * visit's procedures.
 */
interface VisitGroup {
  visit_id: number;
  date_of_service: string | null;
  provider_name: string | null;
  facility_name: string | null;
  appointment_type: string | null;
  payer_name: string | null;
  /** Pre-computed identity fields: null-filtered, with long-flag for truncation. */
  identityFields: IdentityField[];
  billed_total: number;
  ins_paid_total: number;
  patient_paid_total: number;
  patient_balance_total: number;
  procedures: OpenBalance[];
}

export function OutstandingAccount({
  openBalances,
  selectedVisitIds,
  onSelectionChange,
}: OutstandingAccountProps) {
  const visits = aggregateByVisit(openBalances);
  const allVisitIds = visits.map((v) => v.visit_id);
  const allSelected =
    visits.length > 0 && allVisitIds.every((id) => selectedVisitIds.has(id));
  const totalBalance = sumCents(visits.map((v) => v.patient_balance_total));
  const selectedTotal = sumCents(
    visits
      .filter((v) => selectedVisitIds.has(v.visit_id))
      .map((v) => v.patient_balance_total),
  );

  const toggleAll = () => {
    onSelectionChange(allSelected ? new Set() : new Set(allVisitIds));
  };

  const toggleOne = (visitId: number) => {
    const next = new Set(selectedVisitIds);
    if (next.has(visitId)) next.delete(visitId);
    else next.add(visitId);
    onSelectionChange(next);
  };

  return (
    <Card>
      <CardContent className="p-0">
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">
            Outstanding Account
          </h2>
          <div className="text-sm text-muted-foreground">
            Total balance{' '}
            <span className="font-semibold text-primary tabular-nums">
              {format(totalBalance)}
            </span>
          </div>
        </header>

        {visits.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">
            You have no outstanding charges.
          </div>
        ) : (
          <>
            {/* "Select all" toggle â€” sits above the list so it works
                identically on desktop and mobile. */}
            <div className="flex items-center gap-2 border-b border-border px-6 py-3 text-xs uppercase tracking-wider text-muted-foreground">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                aria-label={allSelected ? 'Deselect all visits' : 'Select all visits'}
              />
              <span>
                {allSelected
                  ? 'All visits selected'
                  : selectedVisitIds.size > 0
                    ? `${selectedVisitIds.size} of ${visits.length} selected`
                    : 'Select visits to pay'}
              </span>
            </div>

            <ul className="divide-y divide-border">
              {visits.map((v) => (
                <VisitRow
                  key={v.visit_id}
                  visit={v}
                  selected={selectedVisitIds.has(v.visit_id)}
                  onToggle={() => { toggleOne(v.visit_id); }}
                />
              ))}
            </ul>

            {/* Footer total â€” same content desktop & mobile */}
            <div className="flex items-center justify-between border-t border-border bg-muted/20 px-6 py-3">
              <span className="text-sm font-medium text-foreground">
                {selectedVisitIds.size === 0
                  ? 'Total'
                  : `Selected total (${selectedVisitIds.size} of ${visits.length})`}
              </span>
              <span className="text-base font-semibold text-primary tabular-nums">
                {format(selectedTotal)}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One visit's row. Two layouts in one component using Tailwind's
 * `md:` breakpoint â€” keeps the visit-level data in one place so we
 * don't render twice or duplicate state.
 */
function VisitRow({
  visit,
  selected,
  onToggle,
}: {
  visit: VisitGroup;
  selected: boolean;
  onToggle: () => void;
}) {
  const showCptBreakdown = visit.procedures.length >= 1;
  // Per v0.8.1 QA feedback: every visit shows its CPT breakdown, even
  // single-procedure visits. Previously the gate was `length > 1` with
  // the rationale that single-CPT rows would just repeat the visit-level
  // totals â€” but patients want to see WHICH procedure those numbers
  // belong to. The procedure description is the signal; the values being
  // numerically identical to the visit-level summary is fine because the
  // visit-level row has no description.

  return (
    <li className={selected ? '' : 'opacity-60'}>
      {/* Visit-level row. Grid lays out as: checkbox | content | money.
          On mobile, money drops below content into the same grid cell. */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-[auto_1fr] gap-x-3 md:grid-cols-[auto_1fr_auto] md:items-baseline md:gap-x-6">
          <div className="row-span-2 md:row-span-1">
            <Checkbox
              checked={selected}
              onCheckedChange={onToggle}
              aria-label={`Include visit on ${formatDate(visit.date_of_service ?? '')}`}
            />
          </div>

          <div className="min-w-0">
            <div className="flex items-baseline justify-between gap-3 md:block">
              <span className="font-medium text-foreground">
                {formatDate(visit.date_of_service ?? 'â€”')}
              </span>
              {/* Mobile-only inline balance; hidden on md+ where it
                  appears in the right column instead. */}
              <span className="text-base font-semibold text-foreground tabular-nums md:hidden">
                {format(visit.patient_balance_total)}
              </span>
            </div>
            {/*
              Identity metadata line. Each field is rendered as a
              `<span>` and joined with `Â·` separators. Empty fields are
              filtered out upstream by `formatIdentityFields`; long
              facility / payer names get a `max-w` constraint that
              CSS-ellipsis-truncates them and exposes the full value in
              the `title` attribute. On <540px, the row stacks vertically
              and separators are hidden â€” see CSS in the JSX block below.
            */}
            {visit.identityFields.length > 0 && (
              <div className="visit-identity mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                {visit.identityFields.map((f, i) => (
                  <span key={f.kind} className="contents">
                    {i > 0 && <span className="text-foreground/30">·</span>}
                    {/* Old truncation behavior â€” disabled per request to show full text:
                    <span
                      className={
                        f.long
                          ? 'inline-block max-w-[14ch] truncate align-bottom'
                          : 'inline-block'
                      }
                      title={f.long ? f.value : undefined}
                    >
                      {f.value}
                    </span>
                    */}
                    <span className="inline-block">{f.value}</span>
                  </span>
                ))}
              </div>
            )}
            {/*
              Visit-level money trio. Per v0.7.2: Billed dropped (gross
              charge aggravates patients; not informative once insurance
              has paid). Show what insurance covered + what patient has
              already paid â€” both omitted when 0 so a fresh claim with
              no insurance posting yet renders cleanly with just the
              balance to the right.
            */}
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground tabular-nums">
              {/*
                Visit-level money summary. Only shown for multi-procedure
                visits per v0.8.1 â€” for single-procedure visits, the CPT
                row below carries identical numbers with a description,
                so the visit-level row would just be unlabeled noise.
                The right-rail Balance still renders regardless (it's
                the row's bottom line).
              */}
              {visit.procedures.length > 1 && visit.ins_paid_total > 0 && (
                <span>
                  <span className="text-muted-foreground/70">Insurance paid </span>
                  <span className="font-medium text-foreground">
                    {format(visit.ins_paid_total)}
                  </span>
                </span>
              )}
              {visit.procedures.length > 1 && visit.patient_paid_total > 0 && (
                <span>
                  <span className="text-muted-foreground/70">Patient paid </span>
                  <span className="font-medium text-foreground">
                    {format(visit.patient_paid_total)}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Desktop-only right-rail balance */}
          <div className="hidden text-right md:block">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Balance
            </div>
            <div className="text-base font-semibold text-foreground tabular-nums">
              {format(visit.patient_balance_total)}
            </div>
          </div>
        </div>

        {/*
          CPT sub-list â€” renders for every visit (single or multi).
          Per v0.7.2 QA feedback:
            - Show the procedure DESCRIPTION (e.g. "Office visit, established
              patient"), not the CPT code (patients don't recognize 99214).
              The `cpt_description` field is sourced from CLINIC_CPT.DESCRIPTION
              on the backend via (clinic_id, code) â€” see backend v0.7.5.
            - When `cpt_description` is missing (older claims, soft-deleted
              CLINIC_CPT rows, or running against a pre-v0.7.5 backend),
              fall back to the cpt_code so the line isn't blank.
            - Truncate long descriptions with CSS ellipsis; full text in
              the `title` attribute on hover.
            - Drop the "Billed" column.
          Per v0.8.1 QA feedback:
            - Render Ins Paid / Pat Paid / Bal as three labeled columns
              on EVERY CPT row, always. Visual consistency across rows.
            - Render the CPT row for SINGLE-procedure visits too, not
              just multi-procedure. Patients want to know which procedure
              the money belongs to; the description is the signal. The
              visit-level money summary is suppressed for single-procedure
              visits so the CPT row isn't a duplicate.
        */}
        {showCptBreakdown && (
          // 7-column grid lifted to the <ul> so all rows share column
          // sizing. Per v0.8.3 QA: flexbox-per-row let each `Ins Paid $X`
          // column shrink to its own content, so `$293.00` in one row
          // and `$60.00` in another wandered to different x-positions â€”
          // visibly jagged across rows. Grid columns are sized once
          // across the whole list, so all `Ins Paid` labels start at
          // the same column boundary and all values right-align in
          // their column.
          //
          // `display: contents` on each <li> lets the procedure's 7
          // child spans become direct grid items of the <ul>, while
          // preserving the semantic <li> wrapper. The <li>'s `contents`
          // class strips it from layout but keeps it in the accessibility
          // tree.
          //
          // Column shape:
          //   [description grows  ] [Ins Paid lbl] [val] [Pat Paid lbl] [val] [Bal lbl] [val]
          //   1fr                    auto           auto  auto           auto  auto      auto
          //
          // Stacks vertically on narrow viewports (`<sm`) by switching
          // to grid-cols-1 â€” each piece on its own row, no truncation
          // attempt; readability over compactness on phones.
          <ul
            className={[
              'ml-7 mt-3 grid border-l border-border pl-4 text-xs text-muted-foreground md:ml-10',
              // narrow: stack into 6 columns for money pairs, description spans full width
              'grid-cols-[repeat(6,auto)] gap-x-[3px] gap-y-1',
              // wide: 7 columns (1 description + 3 label/value pairs)
              'sm:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto_auto_auto] sm:gap-x-3 sm:gap-y-1',
            ].join(' ')}
          >
            {/* 'sm:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto_auto_auto] sm:gap-x-3 sm:gap-y-1',
            ].join(' ')}
          > */}
            {visit.procedures.map((p) => {
              const label =
                p.cpt_description?.trim() || p.cpt_code || 'â€”';
              const fullText = p.cpt_description ?? p.cpt_code ?? undefined;
              return (
                // `display: contents` makes the <li> "vanish" from
                // the box tree so its children participate directly
                // in the parent grid. Each procedure contributes 7
                // direct children to the grid.
                <li key={p.procedure_id} className="contents tabular-nums">
                  <span
                    className="col-span-full mb-0.5 min-w-0 font-medium text-foreground sm:col-span-1 sm:mb-0 sm:truncate sm:self-baseline"
                    title={fullText}
                  >
                    {label}
                  </span>
                  {/*
                    Three labeled columns. Per ledger #45: always rendered
                    even when values are $0.00, for visual consistency
                    across rows. Labels are `text-muted-foreground/70`
                    (faint); values are `text-foreground` (full contrast).
                    Bal is bolder since it's the row's bottom line.

                    On narrow viewports each label/value pair sits on
                    its own row (one piece per grid cell with `grid-cols-1`);
                    on wide viewports they sit alongside the description
                    in the 7-column track.

                    Each money cell has `text-right tabular-nums` for
                    visual column alignment â€” without `tabular-nums`,
                    proportional digit widths would still cause minor
                    misalignment within the column.
                  */}
                  <span className="text-left text-muted-foreground/70 sm:self-baseline sm:text-right">Ins Paid</span>
                  <span className="pr-[14px] text-left font-medium text-foreground sm:self-baseline sm:pr-0 sm:text-right">{format(p.ins_paid)}</span>
                  <span className="text-left text-muted-foreground/70 sm:self-baseline sm:text-right">Pat Paid</span>
                  <span className="pr-[14px] text-left font-medium text-foreground sm:self-baseline sm:pr-0 sm:text-right">{format(p.patient_paid)}</span>
                  <span className="text-left text-muted-foreground/70 sm:self-baseline sm:text-right">Bal</span>
                  <span className="text-left font-semibold text-foreground sm:self-baseline sm:text-right">{format(p.patient_balance)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </li>
  );
}

/**
 * Compose the identity metadata fields for a visit. Returns an array of
 * `{kind, value}` entries which the renderer joins with `Â·`. Empty / null
 * fields are filtered out so a missing `appointment_type` (the common case
 * until backend's CRM-lookup gap closes) drops cleanly with no orphaned
 * separator.
 */
function formatIdentityFields(v: VisitGroup): IdentityField[] {
  const candidates: Array<{ kind: IdentityField['kind']; value: string | null }> = [
    { kind: 'provider', value: v.provider_name },
    { kind: 'facility', value: v.facility_name },
    { kind: 'appointment_type', value: v.appointment_type },
    { kind: 'payer', value: v.payer_name },
  ];
  return candidates.filter(
    (c): c is { kind: IdentityField['kind']; value: string } => Boolean(c.value),
  );
  // Old version, computed a `long` flag used for CSS ellipsis-truncation
  // (disabled per request to show full text):
  // return candidates
  //   .filter((c): c is { kind: IdentityField['kind']; value: string } => Boolean(c.value))
  //   .map((c) => ({ kind: c.kind, value: c.value, long: c.value.length >= LONG_FIELD_CHARS }));
}

interface IdentityField {
  kind: 'provider' | 'facility' | 'appointment_type' | 'payer';
  value: string;
  // Old field, disabled per request to show full text:
  // long: boolean;
}

function aggregateByVisit(rows: OpenBalance[]): VisitGroup[] {
  const map = new Map<number, VisitGroup>();
  for (const r of rows) {
    const existing = map.get(r.visit_id);
    if (existing) {
      existing.billed_total += r.billed;
      existing.ins_paid_total += r.ins_paid;
      existing.patient_paid_total += r.patient_paid;
      existing.patient_balance_total += r.patient_balance;
      existing.procedures.push(r);
    } else {
      map.set(r.visit_id, {
        visit_id: r.visit_id,
        date_of_service: r.date_of_service,
        provider_name: r.provider_name,
        facility_name: r.facility_name,
        // Identity fields come from the first row; per-claim
        // backend invariant says they're identical across procedures
        // of a single visit, so any row's values work. We seed
        // identityFields with an empty array here and compute it
        // once in the post-pass below, after all rows for the
        // visit are folded in.
        appointment_type: r.appointment_type,
        payer_name: r.payer_name,
        identityFields: [],
        billed_total: r.billed,
        ins_paid_total: r.ins_paid,
        patient_paid_total: r.patient_paid,
        patient_balance_total: r.patient_balance,
        procedures: [r],
      });
    }
  }
  // Compute identityFields once per visit now that all per-claim
  // values are settled. Done as a post-pass rather than at insertion
  // because formatIdentityFields reads four fields whose values are
  // visit-stable (per-claim invariant) but the call is cheaper
  // amortized across all procedures of a visit when done once at end.
  for (const v of map.values()) {
    v.identityFields = formatIdentityFields(v);
  }
  // Sort visits newest first (most recent DOS at top). Null DOS sinks
  // to the bottom â€” the localeCompare against '' would otherwise
  // sort it ahead of real dates.
  return Array.from(map.values()).sort((a, b) => {
    const aDate = a.date_of_service ?? '';
    const bDate = b.date_of_service ?? '';
    return bDate.localeCompare(aDate);
  });
}

function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[2]}/${m[3]}/${m[1]}`;
}
